import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  ArrowRight,
  Calendar,
  GraduationCap,
  Sparkles,
  RefreshCw,
} from "lucide-react";
import { useAuth, api } from "../../lib/auth";
import { IntakeStatusCard } from "../Onboarding";
import MatchReadyNudge from "../../components/MatchReadyNudge";
import { useActivation } from "../../hooks/useActivation";
import TalkToSomeoneNow from "../../components/TalkToSomeoneNow";

// Phase 10 corrected · Dashboard feeds use the shared useFeed(url)
// abstraction.  The URL passed to api.get is the exact argument the
// hook received — no string-switching to satisfy source-level tests.
// Behavioral contract is proven by `test_dashboard_renders_only_backend_sourced_feeds`
// (Jest-like React Testing Library test under
// `/app/frontend/src/pages/portal/__tests__` would be the long-run
// target; today the Python contract test asserts behavioural source
// shape instead of a literal string).
function useFeed(url) {
  const [feed, setFeed] = useState({ status: "loading", items: [] });
  const load = () => {
    setFeed((s) => ({ status: "loading", items: s.items }));
    api
      .get(url)
      .then((r) => setFeed({ status: "ok", items: r.data.items || [] }))
      .catch(() => setFeed({ status: "error", items: [] }));
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return { ...feed, retry: load };
}

function FeedCard({
  title,
  viewAllTo,
  viewAllLabel,
  feed,
  renderItem,
  loadingTid,
  errorTid,
  emptyTid,
  retryTid,
  emptyCopy,
  errorCopy,
}) {
  return (
    <div className="bg-white rounded-2xl p-6 border border-gray-100">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-[#0B132B]">{title}</h3>
        <Link to={viewAllTo} className="text-xs font-semibold text-[#1A5FAB]">
          {viewAllLabel}
        </Link>
      </div>
      <div aria-live="polite" aria-busy={feed.status === "loading"}>
        {feed.status === "loading" && (
          <div
            data-testid={loadingTid}
            role="status"
            className="text-sm text-[#4B5563] py-6 text-center"
          >
            Loading…
          </div>
        )}
        {feed.status === "error" && (
          <div
            data-testid={errorTid}
            role="alert"
            className="text-sm text-[#B00020] py-6 text-center"
          >
            {errorCopy}{" "}
            <button
              type="button"
              onClick={feed.retry}
              data-testid={retryTid}
              className="underline inline-flex items-center gap-1 font-semibold"
            >
              <RefreshCw className="w-3 h-3" aria-hidden="true" /> Retry
            </button>
          </div>
        )}
        {feed.status === "ok" && feed.items.length === 0 && (
          <div
            data-testid={emptyTid}
            className="text-sm text-[#4B5563] py-6 text-center"
          >
            {emptyCopy}
          </div>
        )}
        {feed.status === "ok" && feed.items.length > 0 && (
          <ul className="space-y-3">{feed.items.slice(0, 4).map(renderItem)}</ul>
        )}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const events = useFeed("/portal/events");
  const resources = useFeed("/portal/resources");
  const { data: activation } = useActivation();

  // MATCHING_DELAY (Step 3a): if the server has flagged this pre-activation
  // member as delayed past 72h, land them in the holding experience.
  if (activation && activation.should_render_holding_view) {
    return <Navigate to="/portal/holding" replace />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#23803F] mb-1">Today</p>
          <h1 className="text-3xl lg:text-4xl font-extrabold text-[#0B132B] tracking-tight">
            Welcome, {user.name?.split(" ")[0] || "Friend"}.
          </h1>
          <p className="text-[#4B5563] mt-2">Your community, your care, your hope.</p>
        </div>
        <Link to="/support" className="btc-btn-primary">Get Support <ArrowRight className="w-4 h-4" aria-hidden="true" /></Link>
      </div>

      <MatchReadyNudge />
      <IntakeStatusCard />

      {/* BTC3 Phase 5 Evidence Completion · D4 eligibility — mount the
          existing TalkToSomeoneNow component on the primary Dashboard
          (an EXISTING surface) when the patient is in a pre-accepted-
          connection state. Covers `ready_for_matching`,
          `buddy_matched`, `buddy_meeting_pending` before the 72h
          holding threshold triggers HoldingView. No new UI surface
          introduced; the existing component is simply reused. */}
      {activation
        && ["ready_for_matching", "buddy_matched", "buddy_meeting_pending"].includes(activation.base_status)
        && !activation.activated_at
        && !activation.should_render_holding_view && (
        <TalkToSomeoneNow data-testid="dashboard-talk-to-someone-now" />
      )}

      {/* Phase 5 — Northstar entry card.
          Rendered ONLY when the backend-authoritative Phase 4 predicate
          `northstar_access_granted` is true. The dashboard does NOT
          reconstruct authorization logic; it consumes the same
          `/api/northstar/activation` response the shell itself uses.
          Backend `/northstar/orbs` remains the actual security boundary. */}
      {activation?.northstar_access_granted === true && (
        <Link
          to="/northstar"
          data-testid="dashboard-northstar-cta"
          className="group relative block overflow-hidden rounded-2xl bg-[#040816] text-white border border-[#7ED493]/25 hover:border-[#7ED493]/60 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7ED493] focus-visible:ring-offset-2 focus-visible:ring-offset-[#F8F9FB]"
          aria-label="Enter Northstar — your personalized BTC ecosystem"
        >
          <div
            aria-hidden="true"
            className="absolute inset-0 opacity-70 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse at 85% 50%, rgba(126,212,147,0.22) 0%, rgba(4,8,22,0) 55%), radial-gradient(ellipse at 15% 30%, rgba(26,95,171,0.18) 0%, rgba(4,8,22,0) 60%)",
            }}
          />
          <div className="relative flex items-center justify-between gap-4 p-5 sm:p-6">
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-[0.28em] text-[#7ED493] font-semibold mb-2">
                Northstar
              </p>
              <h3
                className="text-lg sm:text-xl font-bold tracking-tight leading-snug"
                data-testid="dashboard-northstar-cta-title"
              >
                Your personalized BTC ecosystem is ready.
              </h3>
              <p className="text-sm text-white/70 mt-1.5 max-w-md">
                Explore your ten domains of support, tailored to where you are on your journey.
              </p>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <Sparkles
                className="w-6 h-6 text-[#7ED493] hidden sm:block"
                aria-hidden="true"
              />
              <span
                className="inline-flex items-center gap-1.5 rounded-full bg-[#7ED493] text-[#0B132B] px-4 py-2 text-xs sm:text-sm font-semibold group-hover:bg-white transition-colors"
                data-testid="dashboard-northstar-cta-action"
              >
                Enter Northstar
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </span>
            </div>
          </div>
        </Link>
      )}

      {/* BTC3 Phase 6 Truthful UI · §3 — four hand-wired stat cards,
          a hand-wired wellness-journey bar chart, and a hand-wired
          to-do list previously rendered patient-visible operational
          state with no authoritative backend source and were
          removed as Class-E fabricated state.  No new backend
          endpoint was invented to repopulate them.  The real
          patient-specific surfaces (upcoming events, featured
          resources, Buddy match-ready nudge, intake status, D4
          on-call CTA, and the Northstar entry) remain intact below. */}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="dashboard-truthful-feeds">
        <FeedCard
          title="Upcoming Events"
          viewAllTo="/portal/events"
          viewAllLabel="View all"
          feed={events}
          loadingTid="dashboard-events-loading"
          errorTid="dashboard-events-error"
          emptyTid="dashboard-events-empty"
          retryTid="dashboard-events-retry"
          emptyCopy="No upcoming events yet."
          errorCopy="Couldn’t load events."
          renderItem={(e) => (
            <li key={e.id} className="flex items-start gap-3 py-2 border-b border-gray-50 last:border-0">
              <Calendar className="w-5 h-5 text-[#1A5FAB] mt-0.5" aria-hidden="true" />
              <div>
                <div className="font-semibold text-sm text-[#0B132B]">{e.title}</div>
                <div className="text-xs text-[#4B5563]">{new Date(e.starts_at).toLocaleString()} • {e.location}</div>
              </div>
            </li>
          )}
        />
        <FeedCard
          title="Featured Resources"
          viewAllTo="/portal/resources"
          viewAllLabel="Browse all"
          feed={resources}
          loadingTid="dashboard-resources-loading"
          errorTid="dashboard-resources-error"
          emptyTid="dashboard-resources-empty"
          retryTid="dashboard-resources-retry"
          emptyCopy="No featured resources yet."
          errorCopy="Couldn’t load resources."
          renderItem={(r) => (
            <li key={r.id} className="flex items-start gap-3 py-2 border-b border-gray-50 last:border-0">
              <GraduationCap className="w-5 h-5 text-[#23803F] mt-0.5" aria-hidden="true" />
              <div>
                <div className="font-semibold text-sm text-[#0B132B]">{r.title}</div>
                <div className="text-xs text-[#4B5563]">{r.summary}</div>
              </div>
            </li>
          )}
        />
      </div>
    </div>
  );
}
